import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Spinner } from 'react-bootstrap';

const AddUnitLayer = () => {
    const [formData, setFormData] = useState({
        project_id: '',
        type: '',
        price: '',  // Keep as string for formatting
        area: '',
        location: '',
        picture: '',
        numberOfBedrooms: '', // Keep as string for consistent input handling
        numberOfBathrooms: '', // Keep as string for consistent input handling
        finishing: '',
    });

    const [projects, setProjects] = useState([]);
    const [error, setError] = useState([]);  // Keep this, but you might not use it extensively
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const endpoint = '/projects/unpaginated';
            const { data } = await api.get(endpoint);
            if (data.status) {
                setProjects(data.response.data);
            } else {
                console.error('Error fetching projects:', data.message);
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    // Generic change handler for text and select inputs
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
    };

    // Handler for numeric inputs (bedrooms, bathrooms, area)
    const handleNumericChange = (e) => {
        const { id, value } = e.target;
        // Allow only digits and empty string (for clearing the input)
        if (/^\d*$/.test(value)) {
            setFormData({ ...formData, [id]: value });
        }
    };

    // Specific handler for price, with formatting
    const handlePriceChange = (e) => {
        const { value } = e.target;
        const unformattedValue = value.replace(/,/g, ''); // Remove commas

        // Check if the value is a valid number (or empty)
        if (/^\d*$/.test(unformattedValue) || unformattedValue === '') {
            // Format the number with commas (only if not empty)
            const formattedValue = unformattedValue === '' ? '' : Number(unformattedValue).toLocaleString();
            setFormData(prevState => ({
                ...prevState,
                price: formattedValue
            }));
        }
    };


    const handleFileChange = (e, fieldName) => {
        const file = e.target.files[0];
        const maxSize = 2 * 1024 * 1024; // 2MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

        if (!file) return;
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, JPEG, PNG, or GIF files are allowed.');
            return;
        }
        if (file.size > maxSize) {
            toast.error('File size exceeds 2MB.');
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData({ ...formData, [fieldName]: reader.result }); // Save as Base64
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError([]); // Clear errors
        setLoading(true);

        try {
            // Prepare data for sending to the API
            const dataToSend = {
                ...formData,
                price: parseInt(formData.price.replace(/,/g, ''), 10),  // Remove commas, parse to integer
                area: parseInt(formData.area, 10),       // Parse area to integer
                numberOfBedrooms: parseInt(formData.numberOfBedrooms, 10), // Parse to integer
                numberOfBathrooms: parseInt(formData.numberOfBathrooms, 10), // Parse to integer
            };

            // Check for NaN values after parsing, and handle them (e.g., set to null or 0)
            for (const key in dataToSend) {
                if (typeof dataToSend[key] === 'number' && isNaN(dataToSend[key])) {
                    // Choose a default value (null or 0, depending on your API requirements)
                    dataToSend[key] = null;  // Or set to 0:  dataToSend[key] = 0;
                    toast.error(`Invalid number entered for ${key}.  Field has been reset.`);
                }
            }


            const response = await api.post('/units', dataToSend);
            if (response.data.status) {
                toast.success('Unit added successfully!');
                navigate('/units');
            } else {
                // Handle server-side validation errors (though you don't use 'error' state anymore)
                toast.error(response.data.message || 'An error occurred');
            }
        } catch (err) {
            const errorResponse = err.response?.data?.message;
            toast.error(errorResponse || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    {/* Project Selection */}
                                    <div className="mb-20">
                                        <label htmlFor="project_id" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Project <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            id="project_id"
                                            className="form-control radius-8"
                                            value={formData.project_id}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select a Project</option>
                                            {projects
                                                .filter((project) => project.status !== 'rejected')
                                                .map((project) => (
                                                    <option key={project.id} value={project.id}>
                                                        {project.name}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>

                                    {/* Unit Type */}
                                    <div className="mb-20">
                                        <label htmlFor="type" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Unit Type <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            id="type"
                                            className="form-control radius-8"
                                            value={formData.type}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Unit Type</option>
                                            <option value="duplex">Duplex</option>
                                            <option value="apartment">Apartment</option>
                                            <option value="villa">Villa</option>
                                            <option value="studio">Studio</option>
                                            <option value="townhouse">Townhouse</option>
                                            <option value="twinhouse">Twinhouse</option>
                                            <option value="standalone villa">Standalone Villa</option>
                                            <option value="chalet">Chalet</option>
                                            <option value="cabin">Cabin</option>
                                            <option value="office">Office</option>
                                            <option value="clinic">Clinic</option>
                                            <option value="retail">Retail</option>
                                            <option value="serviced studio">Serviced Studio</option>
                                            <option value="serviced apartment">Serviced Apartment</option>
                                            <option value="branded villa">Branded Villa</option>
                                            <option value="branded apartment">Branded Apartment</option>
                                            <option value="beach house">Beach House</option>
                                            <option value="iVilla">iVilla</option>

                                        </select>
                                    </div>

                                    {/* Price */}
                                    <div className="mb-20">
                                        <label htmlFor="price" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Price <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="price"
                                            placeholder="Enter price"
                                            value={formData.price}
                                            onChange={handlePriceChange}
                                            required
                                        />
                                    </div>

                                    {/* Area */}
                                    <div className="mb-20">
                                        <label htmlFor="area" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Area <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"  // Keep as text, handle validation in onChange
                                            className="form-control radius-8"
                                            id="area"
                                            placeholder="Enter area (e.g., 120)"
                                            value={formData.area}
                                            onChange={handleNumericChange} // Use numeric change handler
                                            required
                                        />
                                    </div>

                                    {/* Location */}
                                    <div className="mb-20">
                                        <label htmlFor="location" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Phase (if its only a number, write "Phase" then number ex: Phase 2) <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="location"
                                            placeholder="Enter phase"
                                            value={formData.location}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {/* Number of Bedrooms */}
                                    <div className="mb-20">
                                        <label htmlFor="numberOfBedrooms" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Number of Bedrooms <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text" // Keep as text
                                            className="form-control radius-8"
                                            id="numberOfBedrooms"
                                            placeholder="Enter number of bedrooms"
                                            value={formData.numberOfBedrooms}
                                            onChange={handleNumericChange} // Use the numeric handler
                                            required
                                        />
                                    </div>

                                    {/* Number of Bathrooms */}
                                    <div className="mb-20">
                                        <label htmlFor="numberOfBathrooms" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Number of Bathrooms <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"  // Keep as text
                                            className="form-control radius-8"
                                            id="numberOfBathrooms"
                                            placeholder="Enter number of bathrooms"
                                            value={formData.numberOfBathrooms}
                                            onChange={handleNumericChange} // Use numeric handler
                                            required
                                        />
                                    </div>

                                    {/* Finishing */}
                                    <div className="mb-20">
                                        <label htmlFor="finishing" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Finishing <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            id="finishing"
                                            className="form-control radius-8"
                                            value={formData.finishing}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Finishing</option>
                                            <option value="unfinished">Unfinished</option>
                                            <option value="Semi Finished">Semi Finished</option>
                                            <option value="Fully Finished">Fully Finished</option>
                                            <option value="Fully Finished With ACs">Fully Finished With ACs</option>
                                            <option value="Fully Finished with ACs and Kitchen Cabinets">Fully Finished with ACs and Kitchen Cabinets</option>

                                            <option value="Fully Furnished">Fully Furnished</option>
                                        </select>
                                    </div>

                                    {/* Picture Upload */}
                                    <div className="mb-20">
                                        <label htmlFor="picture" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Unit Main Picture (max 2mb)(kindly add the render not the floor plan)<span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className="form-control radius-8"
                                            id="picture"
                                            onChange={(e) => handleFileChange(e, 'picture')}
                                            required
                                        />
                                    </div>

                                    {/* Buttons */}
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="button"
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                            disabled={loading}
                                        >
                                            {loading ? <Spinner animation="border" size="sm" /> : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUnitLayer;